// src/pages/CartPage.jsx
import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import CartCard from '../components/CardCart';
import OrderHistoryCard from '../components/OrderHistoryCard';
import {
  getCart,
  removeFromCart,
  createManualOrder,
  uploadReceiptForOrder,
  getMyOrders,
} from '../services/orderApi';
import { getProductById } from '../services/productApi';
import { isOrderPaidStatus } from '../utils/orderUtils';
import CodigoQRImage from '../assets/CodigoQR.png';
import '../styles/CartPage.css';
import { useNavigate } from 'react-router-dom';
import { API_BASES } from '../config';


const API = API_BASES.gateway;

export default function CartPage() {
  const { isAuthenticated, role, accessToken, refreshCartCount } = useContext(AuthContext);

  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cartItems, setCartItems] = useState([]);
  const [itemsWithDetails, setItemsWithDetails] = useState([]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('PAYPAL');
  const [receiptFile, setReceiptFile] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [receiptUploadError, setReceiptUploadError] = useState(null);
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState(null);

  const [lastRelevantOrder, setLastRelevantOrder] = useState(null);
  const [isAwaitingManualPaymentReview, setIsAwaitingManualPaymentReview] = useState(false);

  const lastCartLoadRef = useRef(0);
  const loadingCartRef = useRef(false);
  const manualOrderIdRef = useRef(null);

  const [topOffset, setTopOffset] = useState(0);
  const navigate = useNavigate();
  const currency = 'USD';

  // ---- Historial de compras ----
  const [activeTab, setActiveTab] = useState('cart'); // 'cart' | 'history'
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false); // lazy: only fetch once
  const [downloadingHistoryId, setDownloadingHistoryId] = useState(null); // orderId being downloaded

  // Persistencia local: órdenes ya descargadas (para evitar re-descarga accidental)
  const [downloadedOrders, setDownloadedOrders] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('downloadedOrders') || '[]'));
    } catch {
      return new Set();
    }
  });
  const markOrderDownloaded = (idOrOrderId) => {
    const key = String(idOrOrderId);
    const next = new Set(downloadedOrders);
    next.add(key);
    setDownloadedOrders(next);
    localStorage.setItem('downloadedOrders', JSON.stringify([...next]));
  };

  // ---- layout header spacing (single effect)
  useEffect(() => {
    const header =
      document.querySelector('#mainHeader') ||
      document.querySelector('header') ||
      document.querySelector('.navbar');
    const compute = () => {
      const headerH = header?.offsetHeight || 0;
      const bodyPT = parseFloat(getComputedStyle(document.body).paddingTop) || 0;
      setTopOffset(Math.max(headerH - bodyPT + 4, 0));
      document.documentElement.style.setProperty('--header-height', `${headerH}px`);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Carga carrito (con throttle suave)
  useEffect(() => {
    let cancelled = false;
    const loadCart = async () => {
      if (!isAuthenticated || role !== 'ROL_CLIENTE') {
        setCartItems([]);
        setLastRelevantOrder(null);
        setIsAwaitingManualPaymentReview(false);
        setLoading(false);
        return;
      }
      const now = Date.now();
      if (now - lastCartLoadRef.current < 4000) {
        setLoading(false);
        return;
      }
      if (loadingCartRef.current) return;

      loadingCartRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const { data: cart } = await getCart();
        if (cancelled) return;
        setCartItems(Array.isArray(cart?.items) ? cart.items : []);
        lastCartLoadRef.current = Date.now();
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          setCartItems([]);
        } else {
          setError('Error al cargar carrito.');
        }
      } finally {
        if (!cancelled) setLoading(false);
        loadingCartRef.current = false;
      }
    };
    loadCart();
    return () => { cancelled = true; };
  }, [isAuthenticated, role, accessToken]);

  // Enriquecer items del carrito con datos de producto
  useEffect(() => {
    if (!cartItems.length) {
      setItemsWithDetails([]);
      return;
    }
    (async () => {
      const enriched = await Promise.all(
        cartItems.map(async item => {
          try {
            const resp = await getProductById(item.productId);
            return {
              ...item,
              nombre: resp.data.nombre,
              precioIndividual: resp.data.precioIndividual,
              fotografiaUrl: resp.data.fotografiaUrl,
            };
          } catch {
            return { ...item, nombre: 'Producto no disponible', precioIndividual: 0 };
          }
        })
      );
      setItemsWithDetails(enriched);
    })();
  }, [cartItems]);

  const handleRemove = async (productId) => {
    try {
      setLoading(true);
      await removeFromCart(productId);
      setCartItems(cs => cs.filter(i => i.productId !== productId));
      await refreshCartCount(true);
      setLastRelevantOrder(null);
      setIsAwaitingManualPaymentReview(false);
      setCurrentOrderId(null);
      setUploadSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  const total = itemsWithDetails
    .reduce((sum, i) => sum + (i.precioIndividual || 0) * i.quantity, 0)
    .toFixed(2);

  // >>> Crear orden manual
  const handleManualOrderCreation = async () => {
    if (!cartItems.length) return alert('Carrito vacío.');
    setLoading(true);
    setError(null);
    try {
      const itemsReq = itemsWithDetails.map(d => ({
        productId: d.productId,
        name: d.nombre,
        quantity: d.quantity,
        unitPrice: d.precioIndividual,
        sku: String(d.productId),
      }));
      const { data } = await createManualOrder({ currency, totalAmount: +total, items: itemsReq });
      setCurrentOrderId(data.id);
      manualOrderIdRef.current = data.id;
      localStorage.setItem('pendingApprovalOrderId', String(data.id)); // persistir para reanudar
      setLastRelevantOrder(null);
      setIsAwaitingManualPaymentReview(false);
      await refreshCartCount(true);
      alert(`Orden creada (ID ${data.id}). Ahora sube tu comprobante.`);
    } catch {
      setError('No se pudo crear la orden.');
    } finally {
      setLoading(false);
    }
  };

  // >>> Subir comprobante
  const handleReceiptUpload = async () => {
    if (!currentOrderId || !receiptFile) return alert('Orden o archivo inválido.');
    setIsUploadingReceipt(true);
    setReceiptUploadError(null);
    try {
      const fd = new FormData();
      fd.append('file', receiptFile);
      const { data: updatedOrderDto } = await uploadReceiptForOrder(currentOrderId, fd);

      setUploadSuccessMessage('Comprobante subido. Pendiente aprobación.');
      setReceiptFile(null);
      setCurrentOrderId(null);

      // Asegurar id para polling y persistir
      const ensuredId = updatedOrderDto?.id ?? manualOrderIdRef.current ?? currentOrderId;
      manualOrderIdRef.current = ensuredId;
      localStorage.setItem('pendingApprovalOrderId', String(ensuredId));

      setLastRelevantOrder({
        ...updatedOrderDto,
        id: ensuredId,
        paymentStatus: updatedOrderDto?.paymentStatus ?? 'UPLOADED_RECEIPT',
      });
      setIsAwaitingManualPaymentReview(true);

    } catch (e) {
      console.error("Error al subir el comprobante:", e);
      setReceiptUploadError('Error subiendo el comprobante.');
      setLastRelevantOrder(null);
      setIsAwaitingManualPaymentReview(false);
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  // >>> Completar pago (PayPal)
  const handlePaymentCompletion = useCallback(async (orderKey) => {
    try {
      const resp = await axios.get(`${API}/api/orders/${orderKey}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setLastRelevantOrder(resp.data);
    } catch (e) {
      console.error("Error al obtener detalles de la orden después del pago:", e);
      setLastRelevantOrder(null);
    }
    setIsAwaitingManualPaymentReview(false);
    setUploadSuccessMessage(null);
    setCurrentOrderId(null);
  }, [accessToken]);

  // Rehidratación al entrar / cambiar sesión (si existe "última orden")
  const rehydrateFromLatestOrder = useCallback(async () => {
    try {
      if (!isAuthenticated || role !== 'ROL_CLIENTE') return;

      const resp = await axios.get(
        `${API}/api/orders/my-orders/latest`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
        }
      );

      // Si no hay última orden, no mostrar nada especial
      if (resp.status === 404 || !resp.data) return;

      const dto = resp.data;

      if (dto.id) manualOrderIdRef.current = dto.id;

      if (dto.paymentStatus === 'UPLOADED_RECEIPT') {
        setLastRelevantOrder(dto);
        setIsAwaitingManualPaymentReview(true);
        return;
      }

      if (dto.paymentStatus === 'PAID' || dto.paymentStatus === 'PAID_PAYPAL' || !!dto.downloadUrl) {
        setLastRelevantOrder(dto);
        setIsAwaitingManualPaymentReview(false);
      }
    } catch (e) {
      console.debug('rehydrateFromLatestOrder skipped:', e?.message);
    }
  }, [isAuthenticated, role, accessToken]);

  useEffect(() => { rehydrateFromLatestOrder(); }, [rehydrateFromLatestOrder]);

  // Reanudar (y sanear) pendingApprovalOrderId guardado: VERIFICAR SI EXISTE EN BACKEND
  useEffect(() => {
    const pending = Number(localStorage.getItem('pendingApprovalOrderId') || 0);
    if (!pending) return;

    (async () => {
      try {
        const resp = await axios.get(
          `${API}/api/orders/${pending}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
          }
        );

        // Si no existe → limpiar todo y salir
        if (resp.status === 404 || !resp.data) {
          localStorage.removeItem('pendingApprovalOrderId');
          manualOrderIdRef.current = null;
          setIsAwaitingManualPaymentReview(false);
          setLastRelevantOrder(null);
          return;
        }

        const dto = resp.data;

        // Solo consideramos "en revisión" si el backend dice UPLOADED_RECEIPT
        if (dto.paymentStatus === 'UPLOADED_RECEIPT') {
          manualOrderIdRef.current = dto.id;
          setLastRelevantOrder(dto);
          setIsAwaitingManualPaymentReview(true);
        } else {
          // Si ya está pagada → mostrar descarga; si no, liberar
          localStorage.removeItem('pendingApprovalOrderId');
          manualOrderIdRef.current = null;
          setIsAwaitingManualPaymentReview(false);

          if (dto.paymentStatus === 'PAID' || dto.paymentStatus === 'PAID_PAYPAL' || dto.downloadUrl) {
            setLastRelevantOrder(dto);
          } else {
            setLastRelevantOrder(null);
          }
        }
      } catch {
        // si falla red, no toques UI
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // Polling de orden manual en revisión
  useEffect(() => {
    let intervalId;

    const orderIdToPoll =
      lastRelevantOrder?.id ??
      currentOrderId ??
      manualOrderIdRef.current ??
      Number(localStorage.getItem('pendingApprovalOrderId') || 0);

    if (
      isAuthenticated &&
      role === 'ROL_CLIENTE' &&
      isAwaitingManualPaymentReview &&
      orderIdToPoll
    ) {
      intervalId = setInterval(async () => {
        try {
          const resp = await axios.get(`${API}/api/orders/${orderIdToPoll}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            validateStatus: (s) => (s >= 200 && s < 300) || s === 404,
          });

          // 404 → limpiar flags y storage
          if (resp.status === 404 || !resp.data) {
            setIsAwaitingManualPaymentReview(false);
            setLastRelevantOrder(null);
            setUploadSuccessMessage(null);
            manualOrderIdRef.current = null;
            localStorage.removeItem('pendingApprovalOrderId');
            clearInterval(intervalId);
            return;
          }

          const updatedOrder = resp.data;

          if (updatedOrder.paymentStatus === 'PAID' || updatedOrder.paymentStatus === 'PAID_PAYPAL') {
            setLastRelevantOrder(updatedOrder);
            setIsAwaitingManualPaymentReview(false);
            setUploadSuccessMessage(null);
            setCurrentOrderId(null);
            manualOrderIdRef.current = updatedOrder.id ?? manualOrderIdRef.current;
            localStorage.removeItem('pendingApprovalOrderId');
            clearInterval(intervalId);
          } else if (updatedOrder.paymentStatus === 'PAYMENT_REJECTED') {
            alert(`Tu pago para la orden #${orderIdToPoll} ha sido rechazado. Comentario: ${updatedOrder.adminComment || 'N/A'}`);
            setIsAwaitingManualPaymentReview(false);
            setLastRelevantOrder(null);
            setUploadSuccessMessage(null);
            setCartItems([]);
            try { await refreshCartCount(true); } catch { }
            manualOrderIdRef.current = null;
            localStorage.removeItem('pendingApprovalOrderId');
            clearInterval(intervalId);
          } else {
            // seguir esperando (PENDING_PAYMENT o UPLOADED_RECEIPT)
            setLastRelevantOrder(o => ({ ...(o || {}), ...(updatedOrder || {}) }));
          }
        } catch (e) {
          console.error("Error al refrescar estado de la orden en revisión:", e);
        }
      }, 5000);
    }

    return () => { if (intervalId) clearInterval(intervalId); };
  }, [
    isAuthenticated,
    role,
    isAwaitingManualPaymentReview,
    lastRelevantOrder,
    currentOrderId,
    accessToken,
    refreshCartCount,
  ]);

  // ---- Historial: carga lazy al activar la pestaña ----
  const handleHistoryTab = async () => {
    setActiveTab('history');
    if (historyLoaded) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { data } = await getMyOrders();
      const rawOrders = Array.isArray(data) ? data : (data?.content ?? []);

      // Enrich each order's items with product name from the product API
      const enriched = await Promise.all(
        rawOrders.map(async (order) => ({
          ...order,
          items: await Promise.all(
            (order.items || []).map(async (item) => {
              try {
                const { data: product } = await getProductById(item.productId);
                return { ...item, nombre: product.nombre };
              } catch {
                return item; // fallback: will display "Producto #id"
              }
            })
          ),
        }))
      );

      setOrderHistory(enriched);
      setHistoryLoaded(true);
    } catch (e) {
      console.error('Error cargando historial:', e);
      setHistoryError('No se pudo cargar el historial de compras.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---- Core download: fetch → blob → trigger browser save ----
  // Shared by both the cart download button and the history download button.
  const handleOrderDownload = async (order) => {
    const hasPublicUrl = !!order.downloadUrl;
    const endpoint = hasPublicUrl
      ? order.downloadUrl
      : `${API}/api/orders/${order.id ?? order.orderId}/zip`;
    const fetchOpts = hasPublicUrl ? {} : { headers: { Authorization: `Bearer ${accessToken}` } };

    const resp = await fetch(endpoint, fetchOpts);
    if (!resp.ok) {
      const msg = await resp.text().catch(() => '');
      console.error('Download error:', resp.status, msg);
      alert('Error al descargar. Intenta de nuevo.');
      return;
    }
    const blob = await resp.blob();
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i.exec(cd);
    const filename = m?.[2] ?? `pedido-${order.id ?? order.orderId}.zip`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);

    markOrderDownloaded(String(order.id ?? order.orderId ?? ''));
  };

  // ---- Download from history tab ----
  const handleHistoryDownload = async (order) => {
    setDownloadingHistoryId(order.id);
    try {
      await handleOrderDownload(order);
    } catch (e) {
      console.error(e);
      alert('Error al descargar.');
    } finally {
      setDownloadingHistoryId(null);
    }
  };

  // ---- Download from cart tab (post-download cleanup included) ----
  const handleCartDownload = async () => {
    setDownloading(true);
    try {
      await handleOrderDownload(lastRelevantOrder);
      setCartItems([]);
      try { await refreshCartCount(true); } catch { }
      setLastRelevantOrder(null);
      setIsAwaitingManualPaymentReview(false);
      manualOrderIdRef.current = null;
      localStorage.removeItem('pendingApprovalOrderId');
      setSelectedPaymentMethod('PAYPAL');
    } catch (e) {
      console.error(e);
      alert('Error al descargar.');
    } finally {
      setDownloading(false);
    }
  };

  // ---- UI computed ----
  if (loading) return <div className="loading-state-cart">Cargando carrito…</div>;
  if (error) return <div className="error-state-cart">{error}</div>;

  const awaitingManual = lastRelevantOrder?.paymentStatus === 'UPLOADED_RECEIPT';
  const isOrderPaid = isOrderPaidStatus(lastRelevantOrder);

  // 🔒 Bloqueo SOLO si el backend dice UPLOADED_RECEIPT y aún no está pagada
  const isCartLocked = awaitingManual && !isOrderPaid;

  const downloadKey = String(lastRelevantOrder?.id ?? lastRelevantOrder?.orderId ?? '');
  const hasDownloadReady = Boolean(isOrderPaid && lastRelevantOrder && downloadKey && !downloadedOrders.has(downloadKey));

  const showUploadReceiptSection = Boolean(currentOrderId && !isUploadingReceipt);
  // Use cartItems (not itemsWithDetails) to avoid a brief flash while enrichment runs
  const showInitialEmptyCartMessage =
    cartItems.length === 0 &&
    !currentOrderId &&
    !awaitingManual &&
    !hasDownloadReady;

  const showDownloadButton = !!hasDownloadReady && cartItems.length === 0;

  const showCartAndPaymentOptions =
    itemsWithDetails.length > 0 &&
    !currentOrderId &&
    !awaitingManual;

  // Navegación al catálogo con bloqueo
  const goToCatalog = () => {
    if (isCartLocked) {
      alert('Tu pago está en revisión. No puedes agregar productos hasta que el administrador apruebe o rechace el comprobante.');
      return;
    }
    navigate('/catalog');
  };

  return (
    <div className="container cart-page-container" style={{ paddingTop: topOffset }}>
      <div className="cart-header-bar">
        <h2 className="cart-title">Tu Carrito</h2>
        {activeTab === 'cart' && (
          <button
            type="button"
            className="btn-add-products"
            onClick={goToCatalog}
            disabled={isCartLocked}
            title={isCartLocked ? 'Carrito bloqueado: pago en revisión' : 'Agregar más productos'}
          >
            Agregar más productos
          </button>
        )}
      </div>

      {/* ---- Tabs ---- */}
      <ul className="nav nav-tabs cart-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'cart' ? ' active' : ''}`}
            onClick={() => setActiveTab('cart')}
          >
            🛒 Carrito
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'history' ? ' active' : ''}`}
            onClick={handleHistoryTab}
          >
            📋 Historial de Compras
          </button>
        </li>
      </ul>

      {/* ============ PESTAÑA: CARRITO ============ */}
      {activeTab === 'cart' && (<>

      {/* Aviso de bloqueo */}
      {isCartLocked && (
        <div className="cart-alert alert alert-warning">
          <div className="text-center">
            <strong>Pago en revisión.</strong> No podrás agregar ni modificar productos hasta que el administrador apruebe o rechace tu comprobante.
          </div>
        </div>
      )}

      {showInitialEmptyCartMessage && (
        <div className="cart-content-section">
          <p className="text-center text-muted mb-0">No tienes productos en tu carrito.</p>
        </div>
      )}

      {/* Aviso "Comprobante subido" solo si realmente está en UPLOADED_RECEIPT */}
      {awaitingManual && !isOrderPaid && lastRelevantOrder && (
        <div className="cart-alert alert alert-info">
          <div className="text-center">
            <p className="mb-2">
              <strong>¡Comprobante subido!</strong> Tu orden #{lastRelevantOrder.id ?? lastRelevantOrder.orderId} está pendiente de revisión por el administrador.
            </p>
            <small className="d-block">Una vez que el pago sea aprobado, podrás descargar tus archivos aquí.</small>
            {lastRelevantOrder.adminComment && (
              <p className="mt-2 mb-0">
                <strong>Comentario del administrador:</strong> <em>"{lastRelevantOrder.adminComment}"</em>
              </p>
            )}
          </div>
        </div>
      )}

      {uploadSuccessMessage && !awaitingManual && (
        <div className="cart-alert alert alert-success">
          {uploadSuccessMessage}
        </div>
      )}

      {showCartAndPaymentOptions && (
        <div className="cart-content-section">
          {itemsWithDetails.map(item => (
            <CartCard
              key={item.productId}
              item={item}
              quantity={item.quantity}
              onRemove={() => handleRemove(item.productId)}
            />
          ))}
        </div>
      )}

      {showCartAndPaymentOptions && (
        <div className="cart-total-section">
          <h4 className="cart-total-amount">Total: {currency} {total}</h4>
        </div>
      )}

      <div className="payment-options-container">

        {showCartAndPaymentOptions && (
          <div className="flex-fill">
            <div className="payment-method-selector">
              <label className="payment-method-label">Método de pago</label>
              <select
                className="form-select"
                value={selectedPaymentMethod}
                onChange={e => setSelectedPaymentMethod(e.target.value)}
                disabled={!cartItems.length}
              >
                <option value="PAYPAL">Pagar con Tarjeta de Crédito</option>
                <option value="MANUAL_TRANSFER">Pagar con Transferencia Bancaria</option>
              </select>
            </div>

            {selectedPaymentMethod === 'PAYPAL' ? (
              <div className="paypal-buttons-container">
                <PayPalButtons
                  createOrder={async () => {
                    const itemsReq = itemsWithDetails.map(d => ({
                      productId: d.productId,
                      name: d.nombre,
                      quantity: d.quantity,
                      unitPrice: d.precioIndividual,
                      sku: String(d.productId),
                    }));
                    const { data } = await axios.post(
                      `${API}/api/orders`,
                      { currency, totalAmount: +total, items: itemsReq },
                      { headers: { Authorization: `Bearer ${accessToken}` } }
                    );
                    return data.orderId; // PayPal order id
                  }}
                  onApprove={async (data, actions) => {
                    try {
                      await axios.post(
                        `${API}/api/orders/${data.orderID}/capture`,
                        {},
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                      );
                      await actions.order.capture();
                      handlePaymentCompletion(data.orderID);
                    } catch (error) {
                      console.error("Error durante la captura:", error);
                      alert("Hubo un error al procesar tu pago. Por favor, contacta a soporte.");
                    }
                  }}
                  onCancel={(data) => {
                    console.log('Payment cancelled', data);
                    alert('El pago ha sido cancelado.');
                  }}
                  onError={(err) => {
                    console.error('Payment error', err);
                    alert('Ocurrió un error con PayPal. Inténtalo de nuevo.');
                  }}
                />
              </div>
            ) : (
              <div className="manual-transfer-section">
                <h5>Datos de Transferencia Bancaria</h5>
                <div className="row">
                  <div className="col-md-8">
                    <p><strong>Cuenta de Ahorros:</strong> Banco del Pichincha</p>
                    <p><strong>Cédula:</strong> 0106581119</p>
                    <p><strong>Nº Cuenta:</strong> 2204309931</p>
                    <p><strong>Beneficiario:</strong> Edisson Oswaldo Avila Redrovan</p>
                    <p><strong>Email:</strong> edissonavila33@gmail.com</p>
                  </div>
                  <div className="col-md-4">
                    <div className="qr-code-container">
                      <img src={CodigoQRImage} alt="QR transferencia" className="img-fluid" style={{ maxWidth: 180 }} />
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    className="btn-cart-primary"
                    onClick={handleManualOrderCreation}
                    disabled={!cartItems.length}
                  >
                    Confirmar y Subir Comprobante
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showUploadReceiptSection && (
          <div className="flex-fill">
            <div className="upload-receipt-section">
              <h5>Subir Comprobante (Orden #{currentOrderId})</h5>

              <input
                type="file"
                className="form-control"
                onChange={e => setReceiptFile(e.target.files[0])}
                accept=".jpg,.jpeg,.png,.pdf"
              />

              <div className="text-center">
                <button
                  className="btn-cart-success"
                  onClick={handleReceiptUpload}
                  disabled={!receiptFile || isUploadingReceipt}
                >
                  {isUploadingReceipt ? 'Subiendo…' : 'Subir Comprobante'}
                </button>
              </div>

              {receiptUploadError && (
                <div className="cart-alert alert alert-danger mt-3">
                  {receiptUploadError}
                </div>
              )}

              <p className="text-muted text-center mt-3 mb-0">
                <small>Tu carrito permanecerá aquí hasta que el comprobante sea aprobado.</small>
              </p>
            </div>
          </div>
        )}

        {showDownloadButton && (
          <div className="flex-fill text-center">
            <button
              className="btn btn-success"
              disabled={downloading}
              onClick={handleCartDownload}
            >
              {downloading ? 'Preparando…' : 'Descarga tus archivos Aquí'}
            </button>
            <p className="text-success text-center mt-3 mb-0">
              <small>✅ Tu orden está completa y lista para descargar</small>
            </p>
          </div>
        )}
      </div>

      </>)}

      {/* ============ PESTAÑA: HISTORIAL ============ */}
      {activeTab === 'history' && (
        <div className="order-history-section">
          {historyLoading && (
            <p className="text-center text-muted py-4">Cargando historial…</p>
          )}
          {historyError && (
            <div className="cart-alert alert alert-danger">{historyError}</div>
          )}
          {!historyLoading && !historyError && orderHistory.length === 0 && (
            <p className="text-center text-muted py-4">No tienes compras anteriores.</p>
          )}
          {!historyLoading && orderHistory.map(order => (
            <OrderHistoryCard
              key={order.id}
              order={order}
              onDownload={handleHistoryDownload}
              isDownloading={downloadingHistoryId === order.id}
            />
          ))}
        </div>
      )}

    </div>
  );
}
